import React, { useState, useRef } from "react";
import { postRequest } from "../apiRequests/requestApi";
import { useNavigate } from "react-router-dom";
import QrScanner from "react-qr-scanner";

export default function JoinGame({ onBack, gameName }) {
  const [mode, setMode] = useState("select"); // 'select', 'pin', 'qr'
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrError, setQrError] = useState("");
  const [scanning, setScanning] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const handlePinChange = (idx, val) => {
    if (/^[a-zA-Z0-9]?$/.test(val)) {
      const newPin = [...pin];
      newPin[idx] = val;
      setPin(newPin);

      // Focus next input if value is not empty
      if (val && idx < pin.length - 1) {
        inputRefs.current[idx + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newPin = [...pin];
      if (pin[idx]) {
        newPin[idx] = "";
        setPin(newPin);
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
      }
    }
  };

  const handlePinSubmit = async () => {
    setError("");
    if (pin.join("").length !== 6) return;
    setLoading(true);
    try {
      const response = await postRequest({
        url: "/join-lobby",
        data: { lobbyCode: pin.join("").toLocaleLowerCase() },
      });
      if (response.success) {
        // console.log("Join lobby response:", response);
        const lobbyId = response.data?._id;
        const lobbyCode = response.data?.code || pin.join("");
        if (lobbyId && lobbyCode) {
          navigate(`/games/waiting-room/${lobbyId}/${lobbyCode}/${gameName}`);
        } else {
          setError("Invalid lobby data received");
        }
      } else {
        setError(response.message || "Failed to join lobby");
      }
    } catch (err) {
      setError(err.message || "Failed to join lobby");
    }
    setLoading(false);
  };

  const handleScan = async (data) => {
    if (data && !scanning) {
      setScanning(true);
      setQrError("");
      setLoading(true);
      try {
        const response = await postRequest({
          url: "/join-lobby",
          data: { lobbyCode: data },
        });
        if (response.success) {
          // console.log("Join lobby response:", response);
          const lobbyId = response.data?._id;
          const lobbyCode = response.data?.code || data;
          if (lobbyId && lobbyCode) {
            navigate(`/games/waiting-room/${lobbyId}/${lobbyCode}`);
          } else {
            setError("Invalid lobby data received");
          }
        } else {
          setQrError(response.message || "Failed to join lobby");
        }
      } catch (err) {
        setQrError(err.message || "Failed to join lobby");
      }
      setLoading(false);
      setScanning(false);
    }
  };

  const handleError = (err) => {
    setQrError("Camera error: " + err?.message);
  };

  // UI for PIN entry
  const renderPinEntry = () => (
    <div className="w-full max-w-[340px] mx-auto text-center">
      <div className="bg-gradient-to-r from-[#C6BCFF] to-[#3F9217] text-transparent bg-clip-text   font-bold mb-4 text-3xl">
        Join <span className="">Game</span>
      </div>

      <div className="text-white font-bold  mb-6">ENTER PIN</div>

      <div className="flex justify-center gap-3 mb-8">
        {pin.map((digit, idx) => (
          <input
            key={idx}
            ref={(el) => (inputRefs.current[idx] = el)}
            id={`pin-input-${idx}`}
            type="text"
            inputMode="text"
            pattern="[a-zA-Z0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handlePinChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className="w-10 h-16 text-center text-lg font-semibold text-white bg-gray-800 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-[#3F9217]"
            onPaste={(e) => {
              const paste = e.clipboardData
                .getData("text")
                .replace(/\W/g, "")
                .slice(0, 6);
              if (paste.length === 6) {
                setPin(paste.split(""));
                setTimeout(() => {
                  inputRefs.current[5]?.focus();
                }, 0);
                e.preventDefault();
              }
            }}
          />
        ))}
      </div>

      <button
        onClick={handlePinSubmit}
        disabled={loading || pin.join("").length !== 6}
        className={`w-full bg-[#FBBC04] text-white font-bold text-md rounded py-3 mb-4 ${
          loading || pin.join("").length !== 6
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
      >
        {loading ? "Joining..." : "ENTER"}
      </button>

      {error && <div className="text-red-500 mt-2">{error}</div>}
    </div>
  );

  // UI for QR scan
  const renderQRScan = () => (
    <div className="w-full max-w-[340px] mx-auto text-center">
      <div className="bg-gradient-to-r from-[#C6BCFF] to-[#3F9217] text-transparent bg-clip-text font-bold mb-4 mt-6 text-lg">
        Join <span className="">Game</span>
      </div>

      <div className="flex gap-2 justify-center mb-8">
        <button
          onClick={() => setMode("pin")}
          className="flex-1 border-2 border-[#FBBC04] text-white font-bold text-base rounded-lg py-2"
        >
          ENTER CODE
        </button>
        <button
          onClick={() => navigate("/games/lobbies")}
          className="w-full border border-[#FBBC04] text-white font-bold rounded-lg py-3"
        >
          View Active Lobbies
        </button>
      </div>

      <div className="bg-white rounded-[24px] p-6 my-8 flex justify-center items-center">
        <div className="w-[220px] h-[220px]">
          <QrScanner
            delay={300}
            onError={handleError}
            onScan={handleScan}
            style={{ width: "100%", height: "100%" }}
            facingMode="environment"
          />
        </div>
      </div>

      {qrError && <div className="text-red-500 mt-2">{qrError}</div>}

      <button className="w-full bg-[#FBBC04] text-white font-bold text-lg rounded-lg py-4 mb-4">
        ENTER
      </button>
    </div>
  );

  // UI for initial select
  const renderSelect = () => (
    <div className="w-full mx-auto flex flex-col  min-h-[60vh]">
      <div className="w-full flex flex-col items-center">
        <div className="bg-gradient-to-r from-[#C6BCFF] to-[#3F9217] text-transparent bg-clip-text font-bold text-xl mb-8 mt-0">
          Join <span className="">Game</span>
        </div>
        <div className="w-full max-w-xs flex mt-32 flex-col items-center">
          <button
            onClick={() => setMode("pin")}
            className="w-full bg-[#FBBC04] text-white font-bold rounded-lg py-3 mb-6"
          >
            ENTER CODE
          </button>
          <button
            onClick={() => navigate("/games/lobbies")}
            className="w-full border border-[#FBBC04] text-white font-bold rounded-lg py-3"
          >
            View Active Lobbies
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#130828] text-white flex flex-col items-center justify-start px-4 py-8">
      <div className="w-full flex items-center ">
        <button
          onClick={onBack}
          className="text-white text-2xl mr-2 bg-transparent border-none focus:outline-none"
        >
          &larr;
        </button>
      </div>

      {mode === "select" && renderSelect()}
      {mode === "pin" && renderPinEntry()}
      {mode === "qr" && renderQRScan()}
    </div>
  );
}
